import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
    DropdownAtom,
    ModalAtom,
    TextFieldAtom,
} from "../../../components/atoms/atom";
import ButtonAtom from "../../../components/atoms/button/button";
import PROJECT_TICKET_CATEGORY_CONFIG from "../../../constants/project-ticket-category-config";
import { DEFAULT_VAT_PERCENTAGE, GERMAN_VAT_OPTIONS, VALID_GERMAN_VAT_RATES } from "../../../constants/vat-config";
import { useGetProjectCompaniesQuery } from "../../../store/services/projectCompaniesService";
import DeleteConfirmationModal from "../aufmass/components/sections/DeleteConfirmationModal";
import "./add-category-modal.scss";

function AddCategoryModal({ show, onClose, onSubmit, categoryData }) {
    // State for delete confirmation modal for Generals
    const [deleteGeneralIndex, setDeleteGeneralIndex] = useState(null);
    const { t } = useTranslation();
    const { projectId } = useParams();
    const isEdit = !!categoryData;

    const {
        data: projectCompaniesResponse,
        isLoading: companiesLoading,
    } = useGetProjectCompaniesQuery(projectId, {
        skip: !projectId || !show,
    });
    const projectCompanies = projectCompaniesResponse?.items ?? [];

    // Form state
    const [formData, setFormData] = useState({
        name: "",
        vat: DEFAULT_VAT_PERCENTAGE,
        discount: 2,
        cashDiscount: 2,
        cashDiscountDays: 7,
        contractPerformanceSecurityPercent: 10,
        contractPerformanceSecurityWithOrWithoutNachtraege:
            PROJECT_TICKET_CATEGORY_CONFIG.NACHTRAEGE_OPTIONS.WITH_NACHTRAEGE,
        contractPerformanceSecurityCalculatedOrFix:
            PROJECT_TICKET_CATEGORY_CONFIG.CALCULATION_OPTIONS.CALCULATED,
        warrantySecurityPercent: 5,
        warrantySecurityWithOrWithoutNachtraege:
            PROJECT_TICKET_CATEGORY_CONFIG.NACHTRAEGE_OPTIONS.WITH_NACHTRAEGE,
        warrantySecurityCalculatedOrFix:
            PROJECT_TICKET_CATEGORY_CONFIG.CALCULATION_OPTIONS.CALCULATED,
        projectCompanyId: null,
        lvQuantityWarningPercent: 90,
        contractPerformanceSecurityBasis: "net",
        warrantySecurityBasis: "net",
        generals: [],
        isEditable: 1,
    });

    // Error state
    const [errors, setErrors] = useState({});

    // Reset form or populate with data when modal opens
    useEffect(() => {
        if (show) {
            if (categoryData) {
                setFormData({
                    name: categoryData.name || "",
                    vat: categoryData.vat,
                    discount: categoryData.discount,
                    cashDiscount: categoryData.cashDiscount,
                    cashDiscountDays: categoryData.cashDiscountDays,
                    contractPerformanceSecurityPercent:
                        categoryData.contractPerformanceSecurityPercent,
                    contractPerformanceSecurityWithOrWithoutNachtraege:
                        categoryData.contractPerformanceSecurityWithOrWithoutNachtraege,
                    contractPerformanceSecurityCalculatedOrFix:
                        categoryData.contractPerformanceSecurityCalculatedOrFix,
                    warrantySecurityPercent: categoryData.warrantySecurityPercent,
                    warrantySecurityWithOrWithoutNachtraege:
                        categoryData.warrantySecurityWithOrWithoutNachtraege,
                    warrantySecurityCalculatedOrFix:
                        categoryData.warrantySecurityCalculatedOrFix,
                    projectCompanyId: categoryData.projectCompanyId || null,
                    lvQuantityWarningPercent: categoryData.lvQuantityWarningPercent || 90,
                    contractPerformanceSecurityBasis: categoryData.contractPerformanceSecurityBasis || "net",
                    warrantySecurityBasis: categoryData.warrantySecurityBasis || "net",
                    generals: categoryData.generals || [],
                    isEditable: categoryData.isEditable,
                });
            } else {
                setFormData({
                    name: "",
                    vat: DEFAULT_VAT_PERCENTAGE,
                    discount: 2,
                    cashDiscount: 2,
                    cashDiscountDays: 7,
                    contractPerformanceSecurityPercent: 10,
                    contractPerformanceSecurityWithOrWithoutNachtraege:
                        PROJECT_TICKET_CATEGORY_CONFIG.NACHTRAEGE_OPTIONS.WITH_NACHTRAEGE,
                    contractPerformanceSecurityCalculatedOrFix:
                        PROJECT_TICKET_CATEGORY_CONFIG.CALCULATION_OPTIONS.CALCULATED,
                    warrantySecurityPercent: 5,
                    warrantySecurityWithOrWithoutNachtraege:
                        PROJECT_TICKET_CATEGORY_CONFIG.NACHTRAEGE_OPTIONS.WITH_NACHTRAEGE,
                    warrantySecurityCalculatedOrFix:
                        PROJECT_TICKET_CATEGORY_CONFIG.CALCULATION_OPTIONS.CALCULATED,
                    projectCompanyId: null,
                    lvQuantityWarningPercent: 90,
                    contractPerformanceSecurityBasis: "net",
                    warrantySecurityBasis: "net",
                    generals: [],
                    isEditable: 1,
                });
            }
            setErrors({});
        }
    }, [show, categoryData, projectId]);

    // Handle change for all form fields
    const handleChange = (field, value) => {
        setFormData((prev) => {
            const next = { ...prev, [field]: value };
            // When cash discount is 0 %, there is no skonto period - force the
            // days to 0 (the field is also disabled in this state).
            if (field === "cashDiscount" && Number(value) === 0) {
                next.cashDiscountDays = 0;
            }
            return next;
        });
        // Clear field error when user starts typing
        if (errors[field]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    // Handle adding new general item
    const handleAddGeneral = () => {
        setFormData(prev => {
            const newGeneral = {
                id: uuidv4(),
                label: '',
                percent: '',
                basis: 'net'
            };
            return {
                ...prev,
                generals: [...prev.generals, newGeneral]
            };
        });
    };

    // Show confirmation modal, then remove on confirm
    const handleRemoveGeneral = (index) => {
        setDeleteGeneralIndex(index);
    };

    const handleConfirmRemoveGeneral = () => {
        setFormData(prev => ({
            ...prev,
            generals: prev.generals.filter((_, i) => i !== deleteGeneralIndex)
        }));
        setDeleteGeneralIndex(null);
    };

    const handleCancelRemoveGeneral = () => {
        setDeleteGeneralIndex(null);
    };

    // Handle changing general item fields
    const handleGeneralChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            generals: prev.generals.map((general, i) =>
                i === index ? { ...general, [field]: value } : general
            )
        }));
    };

    const isValidDecimal = (value) => {
        // If the value is a number object, convert to string
        const strValue = String(value).trim();

        // Allow empty value
        if (!strValue) {
            return false;
        }

        // Check if it's a valid decimal with up to 2 decimal places and between 0-100
        const regex = /^(100(\.0{1,2})?|\d{1,2}(\.\d{1,2})?)$/;
        return regex.test(strValue);
    };

    const handleSubmit = () => {
        const newErrors = {};

        // Validate required fields
        if (!formData.name.trim()) {
            newErrors.name = "validation.name-required";
        }

        // VAT validation
        if (!VALID_GERMAN_VAT_RATES.includes(Number(formData.vat))) {
            newErrors.vat = "validation.invalid-vat";
        }

        // For numeric fields, check if they are valid percentages
        if (!isValidDecimal(formData.contractPerformanceSecurityPercent)) {
            newErrors.contractPerformanceSecurityPercent =
                "validation.invalid-percentage";
        }

        if (!isValidDecimal(formData.cashDiscount)) {
            newErrors.cashDiscount = "validation.invalid-percentage";
        }

        if (!isValidDecimal(formData.discount)) {
            newErrors.discount = "validation.invalid-percentage";
        }

        if (!isValidDecimal(formData.warrantySecurityPercent)) {
            newErrors.warrantySecurityPercent = "validation.invalid-percentage";
        }

        if (!isValidDecimal(formData.lvQuantityWarningPercent)) {
            newErrors.lvQuantityWarningPercent = "validation.invalid-percentage";
        }

        // Validate cashDiscountDays is a positive integer
        if (
            formData.cashDiscountDays === "" ||
            formData.cashDiscountDays === null ||
            formData.cashDiscountDays === undefined ||
            !Number.isInteger(Number(formData.cashDiscountDays)) ||
            Number(formData.cashDiscountDays) < 0
        ) {
            newErrors.cashDiscountDays = "validation.positive-integer-required";
        }

        // Validate project company is required
        if (!formData.projectCompanyId) {
            newErrors.projectCompanyId = "validation.project-company-required";
        }

        // Validate generals
        if (formData?.generals?.length) {
            formData.generals.forEach((general, index) => {
                if (!general.label || general.label.trim() === "") {
                    newErrors[`generals_${index}_label`] = "validation.label-required";
                }
                if (general.percent === '' || general.percent === null || general.percent === undefined) {
                    newErrors[`generals_${index}_percent`] = "validation.percent-required";
                } else if (isNaN(general.percent) || general.percent < 0 || general.percent > 100) {
                    newErrors[`generals_${index}_percent`] = "validation.invalid-percentage";
                }
            });
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Destructure to exclude isEditable and convert numeric fields to proper number types
        const { isEditable, ...dataToSubmit } = formData;
        const submissionData = {
            ...dataToSubmit,
            name: dataToSubmit.name.trim(),
            contractPerformanceSecurityPercent: parseFloat(
                dataToSubmit.contractPerformanceSecurityPercent,
            ),
            cashDiscount: parseFloat(dataToSubmit.cashDiscount),
            discount: parseFloat(dataToSubmit.discount),
            vat: Number(dataToSubmit.vat),
            warrantySecurityPercent: parseFloat(dataToSubmit.warrantySecurityPercent),
            cashDiscountDays: parseInt(dataToSubmit.cashDiscountDays, 10),
            lvQuantityWarningPercent: parseFloat(dataToSubmit.lvQuantityWarningPercent),
            // Process generals to ensure proper data types
            generals: dataToSubmit.generals.map(general => ({
                ...general,
                percent: general.percent === '' ? '' : parseFloat(general.percent) || 0
            })),
        };

        onSubmit(submissionData);
    };

    // Nachtraege options
    const NACHTRAEGE_OPTIONS = [
        {
            label: t("ticket-category.with-nachtraege-option.WITH_NACHTRAEGE"),
            value: PROJECT_TICKET_CATEGORY_CONFIG.NACHTRAEGE_OPTIONS.WITH_NACHTRAEGE,
        },
        {
            label: t("ticket-category.with-nachtraege-option.WITHOUT_NACHTRAEGE"),
            value: PROJECT_TICKET_CATEGORY_CONFIG.NACHTRAEGE_OPTIONS.WITHOUT_NACHTRAEGE,
        },
    ];

    // Calculation options
    const CALCULATION_OPTIONS = [
        {
            label: t("ticket-category.calculated-or-fix-option.CALCULATED"),
            value: PROJECT_TICKET_CATEGORY_CONFIG.CALCULATION_OPTIONS.CALCULATED,
        },
        {
            label: t("ticket-category.calculated-or-fix-option.FIX"),
            value: PROJECT_TICKET_CATEGORY_CONFIG.CALCULATION_OPTIONS.FIX,
        },
    ];

    // Project company options
    const COMPANY_OPTIONS = projectCompanies.map((projectCompanyRecord) => ({
        label: projectCompanyRecord.company?.name || "Unknown Company",
        value: projectCompanyRecord.id,
    }));

    return (
        <ModalAtom
            title={
                isEdit
                    ? t("projects.edit-category")
                    : t("projects.add-category")
            }
            show={show}
            handleClose={onClose}
            handleSubmit={handleSubmit}
            body={
                <Container>
                    <Row className="mb-3">
                        <Col md={4}>
                            <TextFieldAtom
                                type="text"
                                label={t("ticket-category.name")}
                                placeholder={t(
                                    "ticket-category.enter-category-name",
                                )}
                                required
                                value={formData.name}
                                error={errors.name ? t(errors.name) : ""}
                                onChange={(e) =>
                                    handleChange("name", e.target.value)
                                }
                            />
                        </Col>
                        <Col md={4}>
                            <DropdownAtom
                                label={t("ticket-category.vat")}
                                placeholder={t("ticket-category.vat")}
                                value={formData.vat}
                                required
                                options={GERMAN_VAT_OPTIONS}
                                error={errors.vat ? t(errors.vat) : ""}
                                onChange={(e) => {
                                    const numValue =
                                        e.target.value === ""
                                            ? ""
                                            : Number(e.target.value);
                                    handleChange("vat", numValue);
                                }}
                                disabled={!formData.isEditable}
                            />
                        </Col>
                        <Col md={4}>
                            <TextFieldAtom
                                type="number"
                                label={t("ticket-category.discount")}
                                placeholder={t("ticket-category.discount")}
                                required
                                value={formData.discount}
                                error={
                                    errors.discount ? t(errors.discount) : ""
                                }
                                onChange={(e) =>
                                    handleChange("discount", e.target.value)
                                }
                                className="text-end"
                                suffix="%"
                            />
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col md={4}>
                            <DropdownAtom
                                label={t("ticket-category.supplier")}
                                placeholder={t("ticket-category.select-project-company")}
                                value={formData.projectCompanyId}
                                required
                                options={COMPANY_OPTIONS}
                                loading={companiesLoading}
                                error={errors.projectCompanyId ? t(errors.projectCompanyId) : ""}
                                onChange={(e) =>
                                    handleChange(
                                        "projectCompanyId",
                                        e.target.value === "" ? null : e.target.value,
                                    )
                                }
                                isClearable
                                disabled={!formData.isEditable || !COMPANY_OPTIONS.length}
                            />
                            {!companiesLoading && !COMPANY_OPTIONS.length && (
                                <small className="text-danger d-block mt-1">
                                    {t("ticket-category.no-companies-in-project")}
                                </small>
                            )}
                        </Col>
                        <Col md={4}>
                            <TextFieldAtom
                                type="number"
                                label={t("ticket-category.lv-quantity-warning-percent")}
                                placeholder={"90"}
                                required
                                value={formData.lvQuantityWarningPercent}
                                error={
                                    errors.lvQuantityWarningPercent
                                        ? t(errors.lvQuantityWarningPercent)
                                        : ""
                                }
                                onChange={(e) =>
                                    handleChange(
                                        "lvQuantityWarningPercent",
                                        e.target.value,
                                    )
                                }
                                className="text-end"
                                suffix="%"
                                min={0}
                                max={100}
                            />
                        </Col>
                        <Col md={2}>
                            <TextFieldAtom
                                type="number"
                                label={t("projects.settings.cash-discount")}
                                placeholder={t("projects.settings.cash-discount")}
                                required
                                value={formData.cashDiscount}
                                error={
                                    errors.cashDiscount
                                        ? t(errors.cashDiscount)
                                        : ""
                                }
                                onChange={(e) =>
                                    handleChange("cashDiscount", e.target.value)
                                }
                                className="text-end"
                                suffix="%"
                                min={0}
                                max={100}
                            />
                        </Col>
                        <Col md={2}>
                            <TextFieldAtom
                                type="number"
                                label={t("projects.settings.cash-discount-days")}
                                placeholder={t("projects.settings.cash-discount-days")}
                                required
                                value={formData.cashDiscountDays}
                                error={
                                    errors.cashDiscountDays
                                        ? t(errors.cashDiscountDays)
                                        : ""
                                }
                                onChange={(e) =>
                                    handleChange("cashDiscountDays", e.target.value)
                                }
                                className="text-end"
                                disabled={Number(formData.cashDiscount) === 0}
                            />
                        </Col>
                    </Row>
                    <Row className="mb-3 align-items-end">
                        <Col md={3} className="d-flex align-items-center pb-2">
                            <strong>{t("ticket-category.contract-performance-security")}</strong>
                        </Col>
                        <Col md={2}>
                            <TextFieldAtom
                                type="number"
                                label={t("ticket-category.percentage")}
                                placeholder={"0"}
                                required
                                value={formData.contractPerformanceSecurityPercent}
                                error={
                                    errors.contractPerformanceSecurityPercent
                                        ? t(errors.contractPerformanceSecurityPercent)
                                        : ""
                                }
                                onChange={(e) =>
                                    handleChange(
                                        "contractPerformanceSecurityPercent",
                                        e.target.value,
                                    )
                                }
                                className="text-end"
                                suffix="%"
                            />
                        </Col>
                        <Col md={4}>
                            <DropdownAtom
                                label={t("ticket-category.calculated-or-fix")}
                                placeholder={t("ticket-category.calculated-or-fix")}
                                value={
                                    formData.contractPerformanceSecurityCalculatedOrFix
                                }
                                required
                                options={CALCULATION_OPTIONS}
                                onChange={(e) =>
                                    handleChange(
                                        "contractPerformanceSecurityCalculatedOrFix",
                                        e.target.value,
                                    )
                                }
                            />
                        </Col>
                        {formData.contractPerformanceSecurityCalculatedOrFix !==
                            PROJECT_TICKET_CATEGORY_CONFIG.CALCULATION_OPTIONS.FIX && (
                                <Col md={3}>
                                    <DropdownAtom
                                        label={t("ticket-category.with-nachtraege")}
                                        placeholder={t("ticket-category.with-nachtraege")}
                                        value={
                                            formData.contractPerformanceSecurityWithOrWithoutNachtraege
                                        }
                                        required
                                        options={NACHTRAEGE_OPTIONS}
                                        onChange={(e) =>
                                            handleChange(
                                                "contractPerformanceSecurityWithOrWithoutNachtraege",
                                                e.target.value,
                                            )
                                        }
                                    />
                                </Col>
                            )}
                    </Row>
                    <Row className="mb-3 align-items-end">
                        <Col md={3} className="d-flex align-items-center pb-2">
                            <strong>{t("ticket-category.warranty-security")}</strong>
                        </Col>
                        <Col md={2}>
                            <TextFieldAtom
                                type="number"
                                label={t("ticket-category.percentage")}
                                placeholder={"0"}
                                required
                                value={formData.warrantySecurityPercent}
                                error={
                                    errors.warrantySecurityPercent
                                        ? t(errors.warrantySecurityPercent)
                                        : ""
                                }
                                onChange={(e) =>
                                    handleChange(
                                        "warrantySecurityPercent",
                                        e.target.value,
                                    )
                                }
                                className="text-end"
                                suffix="%"
                            />
                        </Col>
                        <Col md={4}>
                            <DropdownAtom
                                label={t("ticket-category.calculated-or-fix")}
                                placeholder={t("ticket-category.calculated-or-fix")}
                                value={
                                    formData.warrantySecurityCalculatedOrFix
                                }
                                required
                                options={CALCULATION_OPTIONS}
                                onChange={(e) =>
                                    handleChange(
                                        "warrantySecurityCalculatedOrFix",
                                        e.target.value,
                                    )
                                }
                            />
                        </Col>
                        {formData.warrantySecurityCalculatedOrFix !==
                            PROJECT_TICKET_CATEGORY_CONFIG.CALCULATION_OPTIONS.FIX && (
                                <Col md={3}>
                                    <DropdownAtom
                                        label={t("ticket-category.with-nachtraege")}
                                        placeholder={t("ticket-category.with-nachtraege")}
                                        value={
                                            formData.warrantySecurityWithOrWithoutNachtraege
                                        }
                                        required
                                        options={NACHTRAEGE_OPTIONS}
                                        onChange={(e) =>
                                            handleChange(
                                                "warrantySecurityWithOrWithoutNachtraege",
                                                e.target.value,
                                            )
                                        }
                                    />
                                </Col>
                            )}
                    </Row>

                    {/* Generals Section */}
                    <Row className="mb-3">
                        <Col md={12}>
                            <div className="generals-section">
                                <div className="generals-header">
                                    <h6 className="mb-0">{t("ticket-category.generals")}</h6>
                                    <ButtonAtom
                                        type="button"
                                        className="btn btn-outline-primary btn-sm"
                                        onClick={handleAddGeneral}
                                    >
                                        <FontAwesomeIcon icon={faPlus} className="me-1" />
                                        {t("common.add")}
                                    </ButtonAtom>
                                </div>
                                {formData?.generals?.length > 0 ? (
                                    <div className="table-responsive">
                                        <table className="generals-table table table-hover">
                                            <thead>
                                                <tr className="generals-table-header-row">
                                                    <th className="generals-table-label">
                                                        {t("ticket-category.label")}
                                                    </th>
                                                    <th className="generals-table-percentage">
                                                        {t("ticket-category.percentage")}
                                                    </th>
                                                    <th className="generals-table-actions">
                                                        {t("common.actions")}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.generals.map((general, index) => (
                                                    <tr key={general.id}>
                                                        <td>
                                                            <TextFieldAtom
                                                                type="text"
                                                                placeholder={t("ticket-category.enter-label")}
                                                                value={general.label}
                                                                onChange={(e) =>
                                                                    handleGeneralChange(index, 'label', e.target.value)
                                                                }
                                                                error={
                                                                    errors[`generals_${index}_label`]
                                                                        ? t(errors[`generals_${index}_label`])
                                                                        : ""
                                                                }
                                                            />
                                                        </td>
                                                        <td>
                                                            <TextFieldAtom
                                                                type="number"
                                                                placeholder="0"
                                                                value={general.percent}
                                                                onChange={(e) =>
                                                                    handleGeneralChange(index, 'percent', e.target.value)
                                                                }
                                                                suffix="%"
                                                                min={0}
                                                                max={100}
                                                                error={
                                                                    errors[`generals_${index}_percent`]
                                                                        ? t(errors[`generals_${index}_percent`])
                                                                        : ""
                                                                }
                                                                className="text-end"
                                                            />
                                                        </td>
                                                        <td>
                                                            <ButtonAtom
                                                                type="button"
                                                                className="btn btn-outline-danger btn-sm generals-remove-btn"
                                                                onClick={() => handleRemoveGeneral(index)}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </ButtonAtom>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="text-center text-muted py-3">
                                        {t("ticket-category.no-generals")}
                                    </div>
                                )}
                            </div>
                        </Col>
                    </Row>
                    {/* Delete Confirmation Modal for Generals row */}
                    {deleteGeneralIndex !== null && (
                        <DeleteConfirmationModal
                            show={true}
                            onClose={handleCancelRemoveGeneral}
                            onConfirm={handleConfirmRemoveGeneral}
                            title={t("ticket-category.delete-general-title")}
                            message={t("ticket-category.delete-general-message")}
                        />
                    )}
                </Container>
            }
        />
    );
}

export default AddCategoryModal;
